import React, { useContext, useState } from "react";

import { Edit } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { Clipboard } from "react-feather";
import { Link, useParams } from "react-router-dom";
import mainStyles from "styles";

import { UserLastSeen } from "@safetyculture/s12-apis-connect-web/s12/accounts/history/v1/history_pb";
import { FolderWithMembersCount } from "@safetyculture/s12-apis-connect-web/s12/directory/v1/folder_pb";
import { GroupData } from "@safetyculture/s12-apis-connect-web/s12/groups/v1/common_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import {
  Role,
  SubscriptionType,
  Tier,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";
import { InspectionQuota } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import IncreaseUserInspectionQuota from "./increase-user-inspection-quota";
import { updateUserInspectionQuota } from "api/endpoints/user";
import { callOptionsHeaders } from "api/helpers";
import PermissionsDataGrid from "components/org-display/tabs/components/permissions-data-grid";
import ConfirmDialog from "components/shared/confirm-dialog";
import GenericDialog from "components/shared/generic-dialog";
import {
  NO_DISPLAY_DATA,
  pricingPeriodDisplayMapping,
  subscriptionTierDisplayMapping,
  subscriptionTypeNameDisplayMapping,
} from "constants/index";
import {
  MAX_COLLABORATOR_INSPECTION_QUOTA,
  UNLIMITED_INSPECTION_QUOTA,
} from "constants/quota";
import {
  subscriptionSeatTypesMapping,
  userSeatsToString,
} from "constants/seats";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";
import { UserContext } from "context/user-context";
import { mapChargifyURL } from "helpers/env-values-helper";
import { getUserLastSeen } from "helpers/get-user-last-seen";
import { showPrivilegeIcons } from "helpers/privileges";
import { toUUID } from "helpers/s12-helpers";
import {
  getBillingFrequency,
  getBillingProviderName,
} from "helpers/subscriptions";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";
import { truncateString } from "helpers/utils";
import { UrlParamTypes } from "types/org-display";

interface UserOrgsRecordProps {
  row: Role;
  quota: InspectionQuota | undefined;
  userGroups: GroupData[] | undefined;
  userLastSeen: UserLastSeen[] | undefined;
  orgIsOnEnterprisePlan: boolean;
  folders?: FolderWithMembersCount[];
}

const UserOrgsRecord = ({
  row,
  quota,
  userGroups,
  userLastSeen,
  orgIsOnEnterprisePlan,
  folders,
}: UserOrgsRecordProps) => {
  const { user } = useContext(UserContext);
  const { hasPermissions, permissions } = useContext(PermissionsContext);
  const { regionCode, userId } = useParams<UrlParamTypes>();

  const theme = useTheme();
  const classes = mainStyles();

  const { copyToClipboard } = useContext(SnackbarContext);

  const [openQuotaDialog, setOpenQuotaDialog] = useState(false);
  const [newInspectionQuota, setNewInspectionQuota] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { showError } = useContext(SnackbarContext);
  const billingFrequency = getBillingFrequency(row.subscription[0]);
  const billingProviderName = getBillingProviderName(row.subscription[0]);

  const isActive = (active: boolean) => {
    return (
      <Chip
        size="small"
        sx={{
          ...(active ? classes.greenDay : classes.redWine),
          fontWeight: "bold",
          marginBottom: "3px",
        }}
        label={active ? "Active" : "Canceled"}
        data-testid="active-chip"
      />
    );
  };

  const displayBillingCycle = () => {
    const isSubTypeFreeOrg =
      row.subscription[0] &&
      row.subscription[0].type ===
        (SubscriptionType.FREE || SubscriptionType.FREE_ORG);

    if (isSubTypeFreeOrg || !row.subscription[0]) {
      return null;
    }
    return (
      <Typography
        variant="body2"
        style={{
          whiteSpace: "normal",
          wordBreak: "break-all",
        }}
      >
        <b> Billing Cycle: </b>
        {pricingPeriodDisplayMapping[billingFrequency]}
      </Typography>
    );
  };

  const getUserInspectionQuota = (quota?: InspectionQuota) => {
    if (quota) {
      return quota.usageLimit - quota.orgQuota - quota.baseLimit;
    }
    return 0;
  };

  const openChargifyButton = () => {
    const chargifySiteCode =
      row.subscription[0] && row.subscription[0]?.chargify?.site;
    const chargifySubId =
      row.subscription[0] && row.subscription[0]?.chargify?.id;

    if (chargifySiteCode === undefined || chargifySubId === undefined) {
      return;
    }
    const subscriptionURL = `${mapChargifyURL(
      chargifySiteCode,
    )}/subscriptions/${chargifySubId}`;

    return (
      <IconButton
        href={subscriptionURL}
        target="_blank"
        rel="noopener"
        size="small"
        color="primary"
        data-testid="open-cf-btn"
        sx={{ position: "relative", bottom: 2 }}
      >
        <LaunchIcon fontSize="small" />
      </IconButton>
    );
  };

  const displayBillingProviderId = () => {
    if (
      row.subscription[0] &&
      row.subscription[0].chargify &&
      row.subscription[0].chargify.id
    ) {
      return (
        <Typography
          variant="body2"
          style={{
            whiteSpace: "normal",
            wordBreak: "break-all",
          }}
        >
          <b>{billingProviderName} ID: </b>
          {row.subscription[0].chargify.id} {openChargifyButton()}
        </Typography>
      );
    }

    return null;
  };

  const displaySeatComponents = () => {
    if (row.subscription[0] && row.subscription[0].components) {
      const seatComponentEntries = [];
      for (const [key, value] of Object.entries(
        row.subscription[0].components,
      )) {
        if (value && key.match(/userSeats/)) {
          seatComponentEntries.push([key, value]);
        }
      }
      return seatComponentEntries.map(([key, value], index) => (
        <Typography key={`${key}${index}`} align="left" variant="body2">
          <b>{userSeatsToString[key]}</b>: {value}
        </Typography>
      ));
    }
  };

  const displaySubscriptionTier = () => {
    if (
      row.subscription[0] &&
      (row.subscription[0].type === SubscriptionType.FREE ||
        row.subscription[0].type === SubscriptionType.FREE_ORG)
    ) {
      return null;
    } else if (row.subscription[0]) {
      return (
        <Typography
          variant="body2"
          style={{
            whiteSpace: "normal",
            wordBreak: "break-all",
          }}
        >
          <b>Plan: </b>
          {orgIsOnEnterprisePlan ? (
            <>{subscriptionTierDisplayMapping[Tier.ENTERPRISE]}</>
          ) : (
            <>
              {row.subscription[0] &&
                subscriptionTierDisplayMapping[row.subscription[0].tier]}
            </>
          )}
        </Typography>
      );
    } else {
      return (
        <Typography
          variant="body2"
          style={{
            whiteSpace: "normal",
            wordBreak: "break-all",
          }}
        >
          <b>Plan: </b>
          {NO_DISPLAY_DATA}
        </Typography>
      );
    }
  };

  const displayInspectionLimit = () => {
    if (quota && quota.baseLimit == UNLIMITED_INSPECTION_QUOTA)
      return <>Unlimited</>;
    else {
      return <>{quota?.usageLimit || NO_DISPLAY_DATA}</>;
    }
  };

  const displayCurrentUsage = () => {
    if (quota) {
      return <>{quota.currentUsage}</>;
    } else {
      return NO_DISPLAY_DATA;
    }
  };

  const displayUsageRemaining = () => {
    if (quota) {
      if (quota.baseLimit != UNLIMITED_INSPECTION_QUOTA) {
        return <>{quota.usageLimit - quota.currentUsage}</>;
      } else {
        return "Unlimited";
      }
    } else {
      return NO_DISPLAY_DATA;
    }
  };

  const displaySeatType = () => {
    if (user) {
      return subscriptionSeatTypesMapping[user.seatType];
    } else {
      return NO_DISPLAY_DATA;
    }
  };

  const displayBaseQuota = () => {
    if (quota) {
      if (quota.baseLimit != UNLIMITED_INSPECTION_QUOTA) {
        return quota.baseLimit;
      } else {
        return "Unlimited";
      }
    } else {
      return NO_DISPLAY_DATA;
    }
  };

  const displayUserAdditionalQuota = () => {
    if (quota && quota.baseLimit != UNLIMITED_INSPECTION_QUOTA) {
      return <>{quota.usageLimit - quota.baseLimit - quota.orgQuota}</>;
    } else {
      return NO_DISPLAY_DATA;
    }
  };

  const displayOrgAdditionalQuota = () => {
    if (quota && quota.baseLimit != UNLIMITED_INSPECTION_QUOTA) {
      return <>{quota.orgQuota}</>;
    } else {
      return NO_DISPLAY_DATA;
    }
  };
  const displayQuotaStartDate = () => {
    if (!quota) {
      return NO_DISPLAY_DATA;
    }

    if (quota.baseLimit == UNLIMITED_INSPECTION_QUOTA) {
      return NO_DISPLAY_DATA;
    }

    if (quota.startDate) {
      return (
        <>{formatDateTimeDisplay(convertTimestampToDate(quota.startDate))}</>
      );
    } else {
      return NO_DISPLAY_DATA;
    }
  };

  const displayQuotaEndDate = () => {
    if (!quota) {
      return NO_DISPLAY_DATA;
    }

    if (quota.baseLimit == UNLIMITED_INSPECTION_QUOTA) {
      return NO_DISPLAY_DATA;
    }
    if (quota.endDate) {
      const endDate = convertTimestampToDate(quota.endDate);
      if (endDate.getFullYear() === 9999) {
        return <>Forever</>;
      }
      return <>{formatDateTimeDisplay(endDate)}</>;
    } else {
      return NO_DISPLAY_DATA;
    }
  };

  const handleIncreaseInspectionQuota = () => {
    if (quota && user) {
      setIsLoading(true);
      const userId = user.id;
      updateUserInspectionQuota(
        {
          userId: toUUID(userId),
          orgId: toUUID(row.id),
          quota: newInspectionQuota,
        },
        callOptionsHeaders(regionCode, userId),
      )
        .then((res) => {
          quota.usageLimit = res.quota?.usageLimit || quota.usageLimit;
        })
        .catch((e) => showError(e))
        .finally(() => setIsLoading(false));
    }
  };

  const isActiveInOrg = () => {
    return !row.inactiveMembers.includes(userId);
  };

  const displayFolders = () => {
    if (!folders || folders.length === 0) {
      return NO_DISPLAY_DATA;
    }

    // Sort folders by folder name (case-insensitive)
    const sortedFolders = [...folders].sort((a, b) => {
      const nameA = a.folder?.name?.toLowerCase() || "";
      const nameB = b.folder?.name?.toLowerCase() || "";
      return nameA.localeCompare(nameB);
    });

    return sortedFolders.map((folder) => {
      const folderId = folder.folder?.id ?? folder.folder?.name ?? "";
      return (
        <React.Fragment key={folderId}>
          <Typography
            variant="body2"
            display="inline"
            style={{
              whiteSpace: "normal",
              wordBreak: "break-all",
            }}
          >
            <b>
              {folder.folder?.name} ({folder.folder?.metaLabel}){" "}
            </b>
          </Typography>
          <br />
        </React.Fragment>
      );
    });
  };

  const displayUserGroups = () => {
    if (!userGroups) {
      return NO_DISPLAY_DATA;
    }

    if (userGroups && userGroups.length === 0) {
      return "This user does not belong to any groups in this organisation.";
    }

    return userGroups.map((group) => {
      return (
        <React.Fragment key={group.id}>
          <Typography
            variant="body2"
            display="inline"
            style={{
              whiteSpace: "normal",
              wordBreak: "break-all",
            }}
          >
            <b>{group.name} </b>
          </Typography>
          <br />
          <Box
            onClick={() => copyToClipboard(group.id)}
            sx={classes.clickable}
            display="inline"
            data-testid={"copy-to-clipboard"}
          >
            <Typography
              variant="body2"
              display="inline"
              data-testid={"user-id"}
              style={{
                whiteSpace: "normal",
                wordBreak: "break-all",
              }}
            >
              <b>ID: </b>
              {group.id}{" "}
            </Typography>
            <Clipboard
              color={theme.palette.text.primary}
              size={"1rem"}
              style={{ position: "relative", top: 2 }}
            />
          </Box>
          <br />
        </React.Fragment>
      );
    });
  };

  const displaySeatDetails = () => {
    if (
      row.subscription[0] &&
      (row.subscription[0].type === SubscriptionType.FREE ||
        row.subscription[0].type === SubscriptionType.FREE_ORG)
    ) {
      return NO_DISPLAY_DATA;
    } else {
      return (
        <>
          {/* <Typography
            variant="body2"
            display="inline"
            style={{
              whiteSpace: "normal",
              wordBreak: "break-all",
            }}
            data-testid={"seat-type"}
          >
            <b>Seat Type: </b>
            {displaySeatType()}
          </Typography>
          <br /> */}
          {isLoading ? (
            <>
              <Skeleton animation="wave" width="160px" />
              <Skeleton animation="wave" width="80px" />
              <Skeleton animation="wave" width="130px" />
            </>
          ) : (
            <>
              <TableContainer>
                <Table
                  size="small"
                  padding="none"
                  sx={{
                    [`& .${tableCellClasses.root}`]: {
                      borderBottom: "none",
                    },
                  }}
                >
                  <TableBody>
                    <TableRow key="seat-type">
                      <TableCell colSpan={2}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span>
                            <b>Seat Type</b>
                          </span>
                          <span data-testid={"seat-type"}>
                            <b>{displaySeatType()}</b>
                          </span>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow key="default-seat-quota">
                      <TableCell colSpan={2}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            paddingLeft: "10px",
                          }}
                        >
                          <span>{displaySeatType()} Seat Quota</span>
                          <span data-testid={"base-quota"}>
                            {displayBaseQuota()}
                          </span>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow key="additional-org-quota">
                      <TableCell colSpan={2}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            paddingLeft: "10px",
                          }}
                        >
                          <span>Additional Org Quota</span>
                          <span data-testid={"additional-org-quota"}>
                            +{displayOrgAdditionalQuota()}
                          </span>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow key="additional-user-quota">
                      <TableCell colSpan={2}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            paddingLeft: "10px",
                          }}
                        >
                          <span>
                            Additional User Quota
                            <Tooltip title="Modify additional user inspection quota">
                              <span>
                                <IconButton
                                  color="primary"
                                  disabled={
                                    permissions.isPermissionsLoading ||
                                    !hasPermissions([
                                      ActionType.UPDATE_USER_INSPECTION_QUOTA,
                                    ]) ||
                                    (quota &&
                                      quota.baseLimit ==
                                        UNLIMITED_INSPECTION_QUOTA)
                                  }
                                  sx={{
                                    position: "relative",
                                    bottom: 2,
                                    left: 5,
                                    padding: 1,
                                  }}
                                  onClick={() => {
                                    setOpenQuotaDialog(true);
                                  }}
                                  data-testid="increase-inspection-quota-button"
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </span>
                          <span data-testid={"additional-user-quota"}>
                            +{displayUserAdditionalQuota()}
                          </span>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow key="total-inspection-quota">
                      <TableCell colSpan={2}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span>
                            <b>Total Inspection Quota </b>
                            <Tooltip title="A user's inspection quota is calculated by the sum of the seat type's default inspection quota limit, additional user inspection quota and additional org inspection quota">
                              <IconButton size="small" sx={{ padding: "0px" }}>
                                <InfoIcon />
                              </IconButton>
                            </Tooltip>
                          </span>
                          <span data-testid={"total-inspection-quota"}>
                            <b>{displayInspectionLimit()}</b>
                          </span>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow key="spacer">
                      <TableCell>&nbsp;</TableCell>
                      <TableCell>&nbsp;</TableCell>
                    </TableRow>
                    <TableRow key="quota-start">
                      <TableCell colSpan={2}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span>
                            <b>Quota Start</b>
                          </span>
                          <Box
                            sx={{
                              textAlign: "right",
                              fontFamily: "monospace",
                              fontSize: "small",
                            }}
                            paddingLeft={"5px"}
                            data-testid={"quota-start-date"}
                          >
                            {displayQuotaStartDate()}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow key="quota-end">
                      <TableCell colSpan={2}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span>
                            <b>Quota End</b>
                          </span>
                          <Box
                            sx={{
                              textAlign: "right",
                              fontFamily: "monospace",
                              fontSize: "small",
                            }}
                            paddingLeft={"5px"}
                            data-testid={"quota-end-date"}
                          >
                            {displayQuotaEndDate()}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow key="inspections-started">
                      <TableCell colSpan={2}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span>
                            <b>Inspections Started </b>
                          </span>
                          <span data-testid={"inspection-usage-current"}>
                            {displayCurrentUsage()}
                          </span>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow key="inspections-remaining">
                      <TableCell colSpan={2}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <span>
                            <b>Inspections Remaining</b>
                          </span>
                          <span data-testid={"inspection-usage-remaining"}>
                            {displayUsageRemaining()}
                          </span>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              {/* <Typography
                variant="body2"
                display="inline"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-all",
                }}
                data-testid={"inspection-usage"}
              >
                <b>Inspection Usage: </b>
                {displayInspectionUsage()}
              </Typography>
              <br />
              <Typography
                variant="body2"
                display="inline"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-all",
                }}
                data-testid={"base-quota"}
              >
                <b>Base Quota: </b>
                {displayBaseQuota()}
              </Typography>
              <br />
              <Typography
                variant="body2"
                display="inline"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-all",
                }}
                data-testid={"additional-user-quota"}
              >
                <b>Additional User Quota: </b>
                {displayUserAdditionalQuota()}
              </Typography>
              <br />
              <Typography
                variant="body2"
                display="inline"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-all",
                }}
                data-testid={"additional-org-quota"}
              >
                <b>Additional Org Quota: </b>
                {displayOrgAdditionalQuota()}
              </Typography>
              <br />
              <Typography
                variant="body2"
                display="inline"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-all",
                }}
                data-testid={"quota-start-date"}
              >
                <b>Quota Start: </b>
                {displayQuotaStartDate()}
              </Typography>
              <br />
              <Typography
                variant="body2"
                display="inline"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-all",
                }}
                data-testid={"quota-end-date"}
              >
                <b>Quota End: </b>
                {displayQuotaEndDate()}
              </Typography> */}
            </>
          )}
          <br />
          {/* <Button
            className={clsx(classes.spaceTopSmall, classes.spaceRightXSmall)}
            variant="contained"
            color="primary"
            disabled={
              !hasPermissions([ActionType.UPDATE_USER_INSPECTION_QUOTA]) ||
              (quota && quota.baseLimit == UNLIMITED_INSPECTION_QUOTA)
            }
            onClick={() => {
              setOpenQuotaDialog(true);
            }}
            data-testid="increase-inspection-quota-button"
            startIcon={
              permissions.isPermissionsLoading ? (
                <Autorenew className="spin-1s" />
              ) : null
            }
          >
            Modify User Quota
          </Button> */}
        </>
      );
    }
  };

  const [openPermissionSetsDialog, setOpenPermissionSetsDialog] =
    useState(false);

  return (
    <>
      <TableRow key={row.id}>
        <TableCell
          align="left"
          style={{ verticalAlign: "top", paddingLeft: "30px" }}
        >
          <Typography
            variant="body2"
            component="div"
            color="primary"
            style={{
              whiteSpace: "normal",
              wordBreak: "break-all",
            }}
          >
            <Link
              to={{
                pathname: `/organisation/${regionCode}/${row.id}`,
              }}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <b>{row.name} </b>
            </Link>
            {row.subscription[0] && isActive(row.subscription[0].active)}
          </Typography>
          <br />
          <Typography
            variant="body2"
            display="inline"
            style={{
              whiteSpace: "normal",
              wordBreak: "break-all",
            }}
          >
            <b>Users: </b>
            {row.members.length}
          </Typography>
          <br />
          <Typography
            variant="body2"
            display="inline"
            style={{
              whiteSpace: "normal",
              wordBreak: "break-all",
            }}
          >
            <b>Groups: </b>
            {userGroups ? userGroups.length : 0}
          </Typography>
          <br />
          <Box
            onClick={() => copyToClipboard(row.id)}
            sx={classes.clickable}
            display="inline"
            data-testid={"copy-to-clipboard"}
          >
            <Typography
              variant="body2"
              display="inline"
              data-testid={"user-id"}
              style={{
                whiteSpace: "normal",
                wordBreak: "break-all",
              }}
            >
              <b>ID: </b>
              {row.id}{" "}
            </Typography>
            <Clipboard
              color={theme.palette.text.primary}
              size={"1rem"}
              style={{ position: "relative", top: 2 }}
            />
          </Box>
          <br />
          <Typography
            variant="body2"
            display="inline"
            style={{
              whiteSpace: "normal",
              wordBreak: "break-all",
            }}
            data-testid="user-status-label"
          >
            <b>User Status: </b>
            {isActiveInOrg() ? "Active" : "Deactivated"}
          </Typography>
          <br />
          <Typography
            variant="body2"
            display="inline"
            style={{
              whiteSpace: "normal",
              wordBreak: "break-all",
            }}
            data-testid="user-last-seen"
          >
            <b>Last Seen: </b>
            {userLastSeen && userLastSeen.length > 0
              ? getUserLastSeen(userLastSeen[0].lastSeen)
              : getUserLastSeen(undefined)}
          </Typography>
        </TableCell>

        <TableCell align="left" style={{ verticalAlign: "top" }}>
          {displayUserGroups()}
        </TableCell>

        <TableCell align="left" style={{ verticalAlign: "top" }}>
          {displayFolders()}
        </TableCell>

        <TableCell align="left" style={{ verticalAlign: "top" }}>
          {user.permissionsSetIdentifier ? (
            <Tooltip
              title={"Permission set: " + user.permissionsSetIdentifier?.name}
            >
              <Button
                onClick={() => {
                  setOpenPermissionSetsDialog(true);
                }}
                data-testid="permission-set-details-button"
                style={{ padding: 0 }}
              >
                {truncateString(user.permissionsSetIdentifier?.name)}
              </Button>
            </Tooltip>
          ) : null}
          <Box display="flex" data-testid={"privileges-icon-container"}>
            {user.privilegesEntries &&
              showPrivilegeIcons(user.privilegesEntries)}
          </Box>
        </TableCell>

        {row.subscription[0] ? (
          <>
            <TableCell align="left" style={{ verticalAlign: "top" }}>
              {displaySubscriptionTier()}
              <Typography
                variant="body2"
                display="inline"
                style={{
                  whiteSpace: "normal",
                  wordBreak: "break-all",
                }}
              >
                <b>Sub Type: </b>
                {row.subscription[0]
                  ? subscriptionTypeNameDisplayMapping[row.subscription[0].type]
                  : NO_DISPLAY_DATA}
              </Typography>
              {displaySeatComponents()}
              {displayBillingCycle()}
              {displayBillingProviderId()}
              <Box
                onClick={() => copyToClipboard(row.subscription[0].id)}
                sx={classes.clickable}
                display="inline"
                data-testid={"copy-to-clipboard"}
              >
                <Typography
                  variant="body2"
                  display="inline"
                  style={{
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  <b>Sub ID: </b>
                  {row.subscription[0] && row.subscription[0].id}
                </Typography>
                <Clipboard
                  color={theme.palette.text.primary}
                  size={"1rem"}
                  className="position-icon"
                />
              </Box>
            </TableCell>
          </>
        ) : (
          <TableCell align="left" style={{ verticalAlign: "top" }}>
            {"This user's organisation does not have a subscription."}
          </TableCell>
        )}
        <TableCell align="left" style={{ verticalAlign: "top" }}>
          {displaySeatDetails()}
        </TableCell>
      </TableRow>

      <ConfirmDialog
        title="Modify Additional User Inspection Quota"
        open={openQuotaDialog}
        setOpen={setOpenQuotaDialog}
        onConfirm={() => {
          handleIncreaseInspectionQuota();
          setOpenQuotaDialog(false);
        }}
        disabled={
          newInspectionQuota < 0 ||
          newInspectionQuota > MAX_COLLABORATOR_INSPECTION_QUOTA ||
          newInspectionQuota === getUserInspectionQuota(quota)
        }
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Update"
      >
        <Alert severity="info" variant="filled" style={{ margin: "2px" }}>
          You are about to modify additional user inspection quota for{" "}
          {user.firstName} {user.lastName}{" "}
          <b>
            from {displayUserAdditionalQuota()} to {newInspectionQuota}
          </b>
          .<br />
          The maximum additional user inspection quota is{" "}
          {MAX_COLLABORATOR_INSPECTION_QUOTA}. <br />A user&apos;s inspection
          quota is calculated by the sum of:
          <ul>
            <li>the seat type&apos;s default inspection quota limit</li>
            <li>additional user inspection quota</li>
            <li>additional org inspection quota</li>
          </ul>{" "}
          <b>
            quota = default quota + user additional quota + org additional quota
          </b>
          <br />
          <p />
          <Link
            to={{
              pathname: "https://safetyculture.atlassian.net/wiki/x/pICA0w",
            }}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            LEARN MORE
          </Link>
        </Alert>
        <IncreaseUserInspectionQuota
          currentQuota={quota}
          maxQuota={MAX_COLLABORATOR_INSPECTION_QUOTA}
          handleNewInspectionQuotaChanged={(newInspectionQuota) => {
            setNewInspectionQuota(newInspectionQuota);
          }}
        />
      </ConfirmDialog>

      <GenericDialog
        title={
          user.permissionsSetIdentifier?.name + " - Permission Set Details"
        }
        width="md"
        hasAckBtn={true}
        acknowledgeBtnLabel="OK"
        open={openPermissionSetsDialog}
        handleAckBtnClick={() => {
          setOpenPermissionSetsDialog(false);
        }}
        handleDialogClose={() => {
          setOpenPermissionSetsDialog(false);
        }}
        data-testid="permission-set-details-dialog"
      >
        <PermissionsDataGrid
          permissions={Object.entries(user.privilegesEntries)}
        />
      </GenericDialog>
    </>
  );
};

export default UserOrgsRecord;
